import React, {
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import styled from "styled-components";
import api from "../../api/config";
import { apiV3 } from "../../api";
import {
  GetDicomwebPatientStudies,
  GetDicomwebStudyThumbnails,
  DeleteStudy,
  ShareStudies,
} from "../../api/Patient";
import { ViewerPath } from "../../api/Authentication";
import {
  fetchZipFile,
  cancelDownload,
  resetCancelDownload,
} from "../../services/downloadService";
import { downloadStudyWithPolling } from "../../utils/download-handler";
import DownloadProgressModal from "../shared/DownloadProgressModal";
import LogsModal from "../shared/LogsModal";
import ShareStudyModal from "../shared/ShareStudyModal";
import SendStudyRecordModal from "../shared/SendStudyRecordModal";
import ConfirmDeleteModal from "../shared/ConfirmDeleteModal";
import ShareStudiesModal from "../shared/ShareStudiesModal";
import DownloadStudiesModal from "../shared/DownloadStudiesModal";
import Notification from "../shared/Notification";
import { ActionButton } from "../shared/action-button";

const InfoTable = styled.div`
  background-color: #000;
  border-radius: 8px;
  flex: ${({ flex }) => flex || 1};
  min-height: 0;
  overflow-y: hidden;

  .loadingContainer {
    margin-top: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: transparent !important;
  flex-grow: 1;
  overflow-y: hidden;
`;

const StyledPaper = styled(Paper)`
  background-color: transparent !important;
  color: #fbfeff !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTableRow = styled(TableRow)`
  td {
    padding: 0px;
    border: none;
  }
  &.selected-row td {
    color: #fff !important;
  }
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
`;

const ScrollableTableBody = styled(TableBody)`
  overflow-y: auto;
  flex-grow: 1;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #1f2a36;
    border-radius: 100px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border: 1px solid #3e526a;
    border-radius: 100px;
  }
`;

const ThumbnailList = styled.div`
  display: flex;
`;

const ThumbnailListItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  & img {
    margin-right: 10px;
  }
`;

const OuterContainer = styled.div`
  max-width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #1f2a36;
    border-radius: 100px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border: 1px solid #3e526a;
    border-radius: 100px;
  }
`;

// Helper function to convert YYYYMMDD to YYYY-MM-DD
const formatStudyDate = (dateString) => {
  if (!dateString || dateString.length !== 8) return dateString;

  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  return `${year}-${month}-${day}`;
};

const PatientStudies = forwardRef(({ patient, openIframeViewer }, ref) => {
  const rowRef = useRef(null);
  const [studies, setStudies] = useState(null);
  const [patientStudyMeta, setPatientStudyMeta] = useState({});
  const [thumbnails, setThumbnails] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [loadingDownloadModal, setLoadingDownloadModal] = useState(false);
  const [isShareStudyModalOpen, setIsShareStudyModalOpen] = useState(false);
  const [isSendStudyRecordModalOpen, setisSendStudyRecordModalOpen] =
    useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState(
    "Starting download..."
  );
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudyForMenu, setSelectedStudyForMenu] = useState(null);
  const [loadingThumbnails, setLoadingThumbnails] = useState({});
  const [downloadQueue, setDownloadQueue] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const isCancelledRef = useRef(false);
  const [groupedStudies, setGroupedStudies] = useState({});
  const [loading, setLoading] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [studyToDelete, setStudyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isShareStudiesModalOpen, setIsShareStudiesModalOpen] = useState(false);
  const [studySendLoading, setStudySendLoading] = useState(false);
  const [isDownloadStudiesModalOpen, setIsDownloadStudiesModalOpen] =
    useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (!patient) return;

    const controller = new AbortController();

    const fetchPatientMetaData = async () => {
      setLoading(true);
      setStudies(null);
      setPatientStudyMeta({});
      setExpandedRows([]);
      setTotalRows(null);
      setThumbnails({});

      try {
        const patientMetaDataRequest = await apiV3.get(
          GetDicomwebPatientStudies.replace("{patient_id}", patient.uuid),
          { signal: controller.signal }
        );
        const patientMeta = patientMetaDataRequest.data.data.map((study) => ({
          ...study,
          StudyDate: formatStudyDate(study.studyDate),
          Modality: study.modalities,
          StudyDescription: study.studyDescription,
          StudyInstanceUID: study.studyUID,
          InstitutionName: study.InstitutionName,
        }));
        setStudies(patientMeta);

        const grouped = patientMeta.reduce((acc, study) => {
          const dateKey = study.StudyDate || "Unknown Date";
          if (!acc[dateKey]) acc[dateKey] = [];
          acc[dateKey].push(study);
          return acc;
        }, {});

        setGroupedStudies(grouped);
        setTotalRows(patientMeta.length);
        const studyUIDs = patientMeta.map((study) => study.StudyInstanceUID);

        setExpandedRows(studyUIDs);

        studyUIDs.forEach((studyId) => {
          setLoadingThumbnails((prev) => ({ ...prev, [studyId]: true }));
          // fetchPatientStudyMeta(studyId);
          fetchDicomwebStudyThumbnails(studyId);
        });

        setLoading(false);

        const allStudyUIDs = Object.values(grouped)
          .flat()
          .map((s) => s.StudyInstanceUID)
          .join(",");

        if (typeof patient.setStudyUIDs === "function") {
          patient.setStudyUIDs(allStudyUIDs);
        }
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          console.log("Request cancelled");
        } else {
          console.error("Failed to fetch patient meta data", error);
          setStudies([]);
          setTotalRows(0);
          setLoading(false);
        }
      }
    };

    fetchPatientMetaData();

    return () => {
      controller.abort();
    };
  }, [patient]);

  const prevPatientRef = useRef();

  useEffect(() => {
    if (prevPatientRef.current !== patient) {
      console.log("🟡 patient reference changed");
    } else {
      console.log("🟢 patient reference is the same");
    }
    prevPatientRef.current = patient;
  }, [patient]);

  const fetchDicomwebStudyThumbnails = async (studyId) => {
    try {
      const response = await apiV3.get(GetDicomwebStudyThumbnails.replace("{study_uid}", studyId));
      const thumbnails = response.data.data;

      setPatientStudyMeta((prevState) => ({
        ...prevState,
        [studyId]: {
          ...studies?.find((study) => study.StudyInstanceUID === studyId),
          thumbnails,
        }
      }))

      // thumbnails.forEach((thumbnail) => {
      //   setThumbnails((prevThumbnails) => ({
      //     ...prevThumbnails,
      //     [studyId]: {
      //       ...(prevThumbnails[studyId] || {}),
      //       [thumbnail.seriesInstanceUID]: thumbnail.imageBase64,
      //     },
      //   }));
      // });

      setLoadingThumbnails(
        (prev) => ({
          ...prev,
          [studyId]: false,
        })
      );
    } catch (error) {
      console.error("Failed to fetch dicomweb study thumbnails", error);
    }
  }

  const handleMenuClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    setSelectedStudyForMenu(null);
  };

  const handleDownloadStudyClick = async (study) => {
    let downloadId = null;
    try {
      setLoadingDownloadModal(true);
      resetCancelDownload();

      setDownloadMessage("Initiating download...");
      setDownloadProgress(0);

      const filename = `${study.StudyDate}_${study.Modality}_${study.StudyDescription}_${patient.first_name}${patient.last_name}.zip`;

      // Register download with global manager
      try {
        const { registerDownload, updateDownloadProgress, getActiveDownloads, updateDownloadMetadata } = await import("../../utils/download-manager");

        // Check if there's a queue item for this study
        const queueDownloadId = `queue-${study.StudyInstanceUID}`;
        const allDownloads = getActiveDownloads();
        const queueItem = allDownloads.find(d => d.id === queueDownloadId);

        if (queueItem) {
          // Use the queue item ID as the download ID to avoid duplicates
          downloadId = queueDownloadId;
          // Update the queue item to show it's now downloading with correct metadata
          updateDownloadProgress(queueDownloadId, 0, "downloading");
          // Update metadata to ensure filename is correct
          updateDownloadMetadata(queueDownloadId, {
            filename,
            description: study.StudyDescription || "Unnamed Study",
          });
        } else {
          // No queue item, register as normal download
          downloadId = `study-${study.StudyInstanceUID}-${Date.now()}`;
          registerDownload(downloadId, {
            filename,
            description: study.StudyDescription || "Unnamed Study",
            studyId: study.StudyInstanceUID,
          });
        }
      } catch (error) {
        console.warn("Failed to register download:", error);
        // Fallback: create a new download ID if registration fails
        if (!downloadId) {
          downloadId = `study-${study.StudyInstanceUID}-${Date.now()}`;
        }
      }

      // Use the new download system with polling support
      console.log(`[handleDownloadStudyClick] Starting download for study: ${study.StudyInstanceUID}`);

      const result = await downloadStudyWithPolling(
        study.StudyInstanceUID,
        filename,
        (status, attempt) => {
          // Job status callback (for 202 responses)
          console.log(`[handleDownloadStudyClick] Job status update: ${status} (attempt ${attempt})`);
          setDownloadMessage(`Processing: ${status} (attempt ${attempt})`);
          // Update global manager
          if (downloadId) {
            try {
              const { updateDownloadProgress } = require("../../utils/download-manager");
              updateDownloadProgress(downloadId, 0, status === "completed" ? "downloading" : "pending");
            } catch (e) { }
          }
        },
        (downloadProgress) => {
          // Download progress callback
          console.log(`[handleDownloadStudyClick] Download progress: ${downloadProgress.toFixed(1)}%`);
          setDownloadProgress(downloadProgress);
          setDownloadMessage(`Downloading: ${downloadProgress.toFixed(1)}%`);
          // Update global manager - use our downloadId
          if (downloadId) {
            try {
              const { updateDownloadProgress } = require("../../utils/download-manager");
              updateDownloadProgress(downloadId, downloadProgress, "downloading");
            } catch (e) { }
          }
        },
        false, // blocking mode - wait for completion
        undefined, // onJobReady (not used in blocking mode)
        undefined, // onJobFailed (not used in blocking mode)
        downloadId // Pass the downloadId so handleDownloadJob uses the same registration
      );

      console.log(`[handleDownloadStudyClick] downloadStudyWithPolling result:`, result);

      if (result.success) {
        if (result.downloadUrl) {
          // Direct download (200 response) - download immediately
          console.log(`[handleDownloadStudyClick] Direct download (200), URL: ${result.downloadUrl}`);
          setDownloadMessage("Downloading file...");
          await fetchZipFile(
            result.downloadUrl,
            filename,
            (percentage) => {
              setDownloadProgress(percentage);
              setDownloadMessage(`Downloading: ${percentage.toFixed(1)}%`);
            },
            downloadId // Pass the download ID so fetchZipFile uses the same registration
          );
          console.log(`[handleDownloadStudyClick] Direct download completed`);
          setDownloadMessage("Download completed!");
          // Note: downloadId was passed to fetchZipFile, so it will be marked as complete there
          // No need to mark it complete again here to avoid duplicate completion
        } else if (result.jobId) {
          // Job-based download (202 response) - already handled by downloadStudyWithPolling
          console.log(`[handleDownloadStudyClick] Job-based download (202) completed, jobId: ${result.jobId}`);
          setDownloadMessage("Download completed!");
          // For job-based downloads, handleDownloadJob uses job_id as downloadId in fetchZipFile
          // We registered with our own downloadId, so we need to mark it as complete
          // The actual download completion is handled in fetchZipFile with job_id
          // We'll mark our registration as complete since downloadStudyWithPolling completed successfully
          if (downloadId) {
            try {
              const { completeDownload, getActiveDownloads } = require("../../utils/download-manager");
              // Check if download with our ID still exists and mark it complete
              const allDownloads = getActiveDownloads();
              const ourDownload = allDownloads.find(d => d.id === downloadId);
              if (ourDownload) {
                console.log(`[handleDownloadStudyClick] Marking PatientStudies registration as complete: ${downloadId}`);
                completeDownload(downloadId, true);
              }
              // Also check if job_id download exists and mark it complete if needed
              // (This handles the case where handleDownloadJob uses job_id as downloadId)
              const jobDownload = allDownloads.find(d => d.id === result.jobId);
              if (jobDownload && jobDownload.status !== "completed") {
                console.log(`[handleDownloadStudyClick] Also marking job download as complete: ${result.jobId}`);
                completeDownload(result.jobId, true);
              }
            } catch (e) {
              console.warn("Failed to complete download in manager:", e);
            }
          }
        } else {
          console.warn(`[handleDownloadStudyClick] Success but no downloadUrl or jobId in result`);
        }
      } else {
        console.error(`[handleDownloadStudyClick] Download failed: ${result.error}`);
        // Mark as failed in global manager
        if (downloadId) {
          try {
            const { completeDownload } = require("../../utils/download-manager");
            completeDownload(downloadId, false);
          } catch (e) { }
        }
        throw new Error(result.error || "Download failed");
      }
    } catch (error) {
      // Mark as failed in global manager
      if (downloadId) {
        try {
          const { completeDownload } = require("../../utils/download-manager");
          completeDownload(downloadId, false);
        } catch (e) { }
      }
      if (error.name === "AbortError" || error.message === "Download was cancelled") {
        console.log("Download cancelled");
        setDownloadMessage("Download cancelled");
      } else {
        console.error("Error downloading study:", error);
        setDownloadMessage(`Failed to download study: ${error.message || "Unknown error"}`);
      }
      throw error;
    } finally {
      setLoadingDownloadModal(false);
    }
  };

  const stopDownload = () => {
    cancelDownload();
    isCancelledRef.current = true;

    // Remove all queue items from global manager
    try {
      const { removeDownload } = require("../../utils/download-manager");
      downloadQueue.forEach((item) => {
        const queueDownloadId = `queue-${item.id}`;
        removeDownload(queueDownloadId);
      });
    } catch (error) {
      console.warn("Failed to remove queue items:", error);
    }

    setDownloadQueue([]);
    setLoadingDownloadModal(false);
    setIsDownloadModalOpen(false);
  };

  useImperativeHandle(ref, () => ({
    downloadAll() {
      handleDownloadAll();
    },
  }));

  const handleDownloadAll = async (studiesToDownload = null) => {
    studiesToDownload = studiesToDownload || studies;

    if (!Array.isArray(studiesToDownload)) {
      studiesToDownload = [studiesToDownload];
    }

    console.log("DownloadAll: Studies to download:", studiesToDownload);

    if (!studiesToDownload || studiesToDownload.length === 0) return;

    isCancelledRef.current = false;

    setDownloadQueue([]);

    setIsDownloadModalOpen(true);
    isCancelledRef.current = false;

    const initialQueue = studiesToDownload.map((s) => ({
      id: s.StudyInstanceUID,
      description: s.StudyDescription || "Unnamed Study",
      status: "pending",
    }));

    setDownloadQueue(initialQueue);

    // Register all queue items with global download manager
    try {
      const { registerDownload } = require("../../utils/download-manager");
      initialQueue.forEach((item) => {
        const queueDownloadId = `queue-${item.id}`;
        registerDownload(queueDownloadId, {
          filename: `${item.description}.zip`,
          description: item.description,
          studyId: item.id,
          status: "pending",
          progress: 0,
          isQueueItem: true,
        });
      });
    } catch (error) {
      console.warn("Failed to register queue items:", error);
    }

    for (let i = 0; i < studiesToDownload.length; i++) {
      if (isCancelledRef.current) {
        console.log("DownloadAll: Cancelled by user");
        break;
      }

      const s = studiesToDownload[i];

      setDownloadQueue((prev) =>
        prev.map((item) =>
          item.id === s.StudyInstanceUID
            ? { ...item, status: "downloading" }
            : item
        )
      );

      // Note: handleDownloadStudyClick will update the queue item status in global manager
      // when it detects the queue item exists, so we don't need to update it here

      try {
        await handleDownloadStudyClick(s);

        if (!isCancelledRef.current) {
          setDownloadQueue((prev) =>
            prev.map((item) =>
              item.id === s.StudyInstanceUID
                ? { ...item, status: "success" }
                : item
            )
          );

          // Remove completed queue item from global manager
          try {
            const { completeDownload } = require("../../utils/download-manager");
            const queueDownloadId = `queue-${s.StudyInstanceUID}`;
            completeDownload(queueDownloadId, true);
          } catch (error) {
            console.warn("Failed to complete queue item:", error);
          }
        }
      } catch (error) {
        if (!isCancelledRef.current) {
          setDownloadQueue((prev) =>
            prev.map((item) =>
              item.id === s.StudyInstanceUID
                ? { ...item, status: "failed" }
                : item
            )
          );

          // Mark failed queue item in global manager
          try {
            const { completeDownload } = require("../../utils/download-manager");
            const queueDownloadId = `queue-${s.StudyInstanceUID}`;
            completeDownload(queueDownloadId, false);
          } catch (error) {
            console.warn("Failed to mark queue item as failed:", error);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (!isCancelledRef.current) {
      if (downloadQueue.every(s => s.status === "success")) {
        setTimeout(() => {
          setIsDownloadModalOpen(false);
        }, 2000);
      }
    }
  }, [downloadQueue]);

  async function handleViewerOpen(
    event,
    selectedStudyForMenu,
    patient,
    setIframeLoading,
    handleMenuClose,
    thumbnail
  ) {
    console.log("Opening viewer for study:", selectedStudyForMenu);
    event.stopPropagation();
    if (!selectedStudyForMenu) return;

    try {
      setIframeLoading(true);
      const nexusToken = localStorage.getItem("token");
      const selectedPatientId = patient.uuid;
      const seriesInstanceUID = thumbnail ? `&SeriesInstanceUIDs=${thumbnail.seriesInstanceUID}` : '';
      const url = `${ViewerPath}?StudyInstanceUIDs=${selectedStudyForMenu.StudyInstanceUID}${seriesInstanceUID}&token=${nexusToken}&patientId=${selectedPatientId}&origin=iframe`;

      openIframeViewer(url);
      handleMenuClose(event);
    } catch (error) {
      console.error("Error opening iframe viewer:", error);
    } finally {
      setIframeLoading(false);
    }
  }

  const confirmDeleteStudy = async () => {
    if (!studyToDelete) return;
    setIsDeleting(true);
    console.log("Deleting study:", studyToDelete.StudyInstanceUID);
    try {
      await api.get(`${DeleteStudy}/${studyToDelete.StudyInstanceUID}`);
      setGroupedStudies((prev) => {
        const newGrouped = { ...prev };
        for (const date in newGrouped) {
          newGrouped[date] = newGrouped[date].filter(
            (s) => s.StudyInstanceUID !== studyToDelete.StudyInstanceUID
          );
          if (newGrouped[date].length === 0) delete newGrouped[date];
        }
        return newGrouped;
      });
      setStudyToDelete(null);
      setIsConfirmDeleteOpen(false);
    } catch (error) {
      console.error("Failed to delete study", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShareStudies = async (selectedStudies, email, days) => {
    setStudySendLoading(true);
    console.log("Sharing studies:", selectedStudies);
    try {
      const endpoint = `${ShareStudies}/${patient.uuid}?targetEmail=${email}&NumberOfDays=${days}`;
      await api.post(endpoint, selectedStudies);
      setNotification({
        open: true,
        message: "Studies Shared Successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error sharing studies:", error);
      setNotification({
        open: true,
        message: "Error sharing studies",
        severity: "error",
      });
    } finally {
      setStudySendLoading(false);
      setIsShareStudiesModalOpen(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <div>
      <InfoTable>
        <StyledTableContainer component={StyledPaper}>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              marginTop: "16px",
            }}
          >
            {/* <StyledButton variant="contained" onClick={handleViewSelected}>
              View Selected
            </StyledButton> */}
            <ActionButton
              onClick={() => setIsDownloadStudiesModalOpen(true)}
              variant="contained"
            >
              Download Images
            </ActionButton>
            <ActionButton
              variant="contained"
              onClick={() => setIsShareStudiesModalOpen(true)}
            >
              Share Images
            </ActionButton>
            {/* <Button
              color="error"
              variant="outlined"
              onClick={() => setSelectedThumbnails([])}
            >
              Clear All
            </Button> */}
          </div>
          {loading || studies === null ? (
            <div className="loadingContainer">
              <CircularProgress style={{ color: "#FBFEFF" }} />
            </div>
          ) : (
            <TableWrapper>
              <Table stickyHeader>
                <ScrollableTableBody>
                  {studies.length === 0 ? (
                    <TableRow style={{ height: "100px" }}>
                      <TableCell
                        style={{ textAlign: "center", height: "100%" }}
                      >
                        No Studies Available
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.keys(groupedStudies)
                      .sort((a, b) => b.localeCompare(a))
                      .map((date) => (
                        <React.Fragment key={date}>
                          <StyledTableRow>
                            <TableCell
                              style={{
                                padding: '0px',
                                paddingTop: '16px',
                              }}
                              colSpan={2}
                            >
                              <Typography
                                style={{
                                  padding: "0",
                                  color: "#FBFEFF",
                                  fontWeight: "bold",
                                  fontSize: "18px",
                                }}
                              >
                                {new Date(date).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                })}
                              </Typography>
                            </TableCell>
                          </StyledTableRow>

                          {groupedStudies[date].map((study, index) => (
                            <React.Fragment key={study.StudyInstanceUID}>
                              <StyledTableRow
                                hover
                                role="checkbox"
                                tabIndex={-1}
                                className={
                                  expandedRows.includes(study.StudyInstanceUID)
                                    ? "selected-row"
                                    : ""
                                }
                              >
                                <TableCell>
                                  {
                                    study.Modality && (
                                      <Typography
                                        style={{
                                          padding: "0",
                                          color: "#FBFEFF",
                                          fontWeight: "bold",
                                          fontSize: "16px",
                                          lineHeight: "130%",
                                          letterSpacing: "1%",
                                          paddingBottom: '12px',
                                          paddingTop: '4px',
                                        }}
                                      >
                                        {study.Modality}
                                      </Typography>
                                    )
                                  }
                                </TableCell>
                              </StyledTableRow>

                              {expandedRows.includes(
                                study.StudyInstanceUID
                              ) && (
                                  <TableRow ref={rowRef}>
                                    <TableCell
                                      style={{
                                        maxWidth: "50px",
                                        padding: '0px',
                                        paddingBottom: '8px',
                                        paddingTop: index === 0 ? '0' : '16px',
                                      }}
                                      colSpan={2}
                                    >
                                      {loadingThumbnails[
                                        study.StudyInstanceUID
                                      ] && (
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              height: "100px",
                                            }}
                                          >
                                            <CircularProgress
                                              style={{ color: "#A5E0FF" }}
                                            />
                                          </div>
                                        )}
                                      {!loadingThumbnails[
                                        study.StudyInstanceUID
                                      ] && patientStudyMeta[
                                        study.StudyInstanceUID
                                      ].thumbnails.length === 0 && (
                                          <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            height: "100px",
                                            gap: "8px",
                                          }}>
                                            No Thumbnail available
                                            <ActionButton
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewerOpen(
                                                  e,
                                                  study,
                                                  patient,
                                                  setIframeLoading,
                                                  handleMenuClose
                                                );
                                              }}
                                              variantType="outlined"
                                            >
                                              Open in Viewer
                                            </ActionButton></div>
                                        )}
                                      <OuterContainer>
                                        <ThumbnailList>
                                          {patientStudyMeta[
                                            study.StudyInstanceUID
                                          ] &&
                                            patientStudyMeta[
                                              study.StudyInstanceUID
                                            ].thumbnails.map((thumbnail) => {
                                              if (!thumbnail.imageBase64) return null;

                                              const base64Image = `data:${thumbnail.imageContentType || "image/jpeg"};base64, ${thumbnail.imageBase64}`;

                                              return (
                                                <ThumbnailListItem
                                                  key={thumbnail.seriesInstanceUID}
                                                  style={{
                                                    position: "relative",
                                                    border: "none",
                                                    borderRadius: "8px",
                                                    paddingRight: "8px",
                                                    boxSizing: "border-box",
                                                  }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewerOpen(
                                                      e,
                                                      study,
                                                      patient,
                                                      setIframeLoading,
                                                      handleMenuClose,
                                                      thumbnail
                                                    );
                                                  }}
                                                >
                                                  <img
                                                    src={base64Image}
                                                    alt="Thumbnail"
                                                    width={150}
                                                    height={150}
                                                    style={{
                                                      border: "1px solid white",
                                                    }}
                                                  />
                                                </ThumbnailListItem>
                                              );
                                            })}
                                        </ThumbnailList>
                                      </OuterContainer>
                                    </TableCell>
                                  </TableRow>
                                )}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))
                  )}
                </ScrollableTableBody>
              </Table>
            </TableWrapper>
          )}
        </StyledTableContainer>
      </InfoTable>
      <DownloadProgressModal
        onClose={() => setIsDownloadModalOpen(false)}
        open={isDownloadModalOpen}
        studies={downloadQueue}
        onCancel={() => stopDownload()}
      />
      <LogsModal
        selectedPatient={patient}
        selectedStudyId={selectedStudy}
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
      />
      <ShareStudyModal
        selectedPatient={patient}
        selectedStudyId={selectedStudy}
        open={isShareStudyModalOpen}
        onClose={() => setIsShareStudyModalOpen(false)}
      />
      <SendStudyRecordModal
        selectedPatient={patient}
        selectedStudyId={selectedStudy}
        open={isSendStudyRecordModalOpen}
        onClose={() => setisSendStudyRecordModalOpen(false)}
      />
      {iframeLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(20, 27, 37, 0.8)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress style={{ color: "#ffffff" }} size={60} />
        </div>
      )}
      <ConfirmDeleteModal
        open={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={confirmDeleteStudy}
        loading={isDeleting}
      />
      <ShareStudiesModal
        open={isShareStudiesModalOpen}
        onClose={() => setIsShareStudiesModalOpen(false)}
        studies={studies || []}
        loading={studySendLoading}
        onSubmit={([selectedIds, email, days]) => {
          console.log("Sharing studies:", selectedIds, email, days);
          handleShareStudies(selectedIds, email, days);
        }}
      />
      <DownloadStudiesModal
        open={isDownloadStudiesModalOpen}
        onClose={() => setIsDownloadStudiesModalOpen(false)}
        studies={studies || []}
        loading={false}
        onSubmit={(selectedIds) => {
          const selected = (studies || []).filter((s) =>
            selectedIds.includes(s.StudyInstanceUID)
          );
          handleDownloadAll(selected);
          setIsDownloadStudiesModalOpen(false);
        }}
      />

      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        handleClose={handleCloseNotification}
      />
    </div>
  );
});

export default PatientStudies;
